import React from 'react';
import { Code2, Globe, Smartphone, Bot, Layers, ShieldCheck } from 'lucide-react';
import {
  SiJavascript, SiReact, SiNodedotjs, SiExpress, SiMongodb, SiTailwindcss, SiNextdotjs, SiGithub,
  SiMysql, SiFirebase, SiTypescript, SiRedux, SiFigma, SiGraphql, SiDocker,
} from 'react-icons/si';
import { Container, Eyebrow, PillButton, navyIn, NAVY_SECTION, Fade, NAVY, LIGHT } from '../../components/home/ui';
import Pricing from '../../components/home/Pricing';
import Faq from '../../components/home/Faq';

const services = [
  { title: 'Custom Software Development', desc: 'Tailored software solutions built to solve real business problems and scale with growth.', icon: Code2, tag: 'Full Stack' },
  { title: 'Web Application Development', desc: 'Fast, modern, and conversion-focused websites and web apps designed for performance.', icon: Globe, tag: 'Web' },
  { title: 'Mobile App Development', desc: 'High-performance apps for Android and iOS with a seamless user experience.', icon: Smartphone, tag: 'Android & iOS' },
  { title: 'AI & Automation Solutions', desc: 'Smart systems, chatbots, and automation that improve efficiency and reduce manual work.', icon: Bot, tag: 'AI / ML' },
  { title: 'Business CMS & WordPress', desc: 'Scalable, easy-to-manage CMS platforms tailored to how your business runs.', icon: Layers, tag: 'CMS' },
  { title: 'Maintenance & Support', desc: 'Reliable support to keep your systems secure, fast, and always running.', icon: ShieldCheck, tag: 'Support' },
];

const technologies = [
  { name: 'React', category: 'Frontend Library', Icon: SiReact, color: '#00b4d8' },
  { name: 'Next.js', category: 'Fullstack Framework', Icon: SiNextdotjs, color: '#111111' },
  { name: 'TypeScript', category: 'Typed Language', Icon: SiTypescript, color: '#3178c6' },
  { name: 'JavaScript', category: 'Core Language', Icon: SiJavascript, color: '#d4a106' },
  { name: 'Tailwind CSS', category: 'Styling & UI', Icon: SiTailwindcss, color: '#06b6d4' },
  { name: 'Node.js', category: 'Backend Runtime', Icon: SiNodedotjs, color: '#22c55e' },
  { name: 'Express.js', category: 'REST APIs', Icon: SiExpress, color: '#475569' },
  { name: 'MongoDB', category: 'NoSQL Database', Icon: SiMongodb, color: '#16a34a' },
  { name: 'MySQL', category: 'Relational DB', Icon: SiMysql, color: '#0284c7' },
  { name: 'Firebase', category: 'Cloud BaaS', Icon: SiFirebase, color: '#f59e0b' },
  { name: 'React Native', category: 'Mobile Apps', Icon: SiReact, color: '#0ea5e9' },
  { name: 'Redux Toolkit', category: 'State Management', Icon: SiRedux, color: '#7c3aed' },
  { name: 'GraphQL', category: 'Query Language', Icon: SiGraphql, color: '#e11d48' },
  { name: 'Docker', category: 'DevOps & Containers', Icon: SiDocker, color: '#2563eb' },
  { name: 'GitHub', category: 'VCS & CI/CD', Icon: SiGithub, color: '#18181b' },
  { name: 'Figma', category: 'UI / UX Design', Icon: SiFigma, color: '#f97316' },
];

const AllServices = () => (
  <>
    {/* ── Hero ── */}
    <section className="relative overflow-hidden bg-linear-to-b from-[#d5d9e3] to-[#e9ecf2] pt-28 lg:pt-32 pb-16">
      <Container className="relative text-center">
        <div
          aria-hidden="true"
          className="pointer-events-none select-none"
        >
          <div className="font-bold uppercase leading-[0.85] tracking-[-0.06em] px-[0.08em] text-[16vw] lg:text-[200px] xl:text-[215px] bg-linear-to-b from-white via-white/85 to-white/0 bg-clip-text text-transparent">
            Services
          </div>
        </div>

        <div className="-mt-[8vw] lg:-mt-24 relative">
          <Eyebrow>What I Offer</Eyebrow>
          <h1 className="mt-4 text-[38px] sm:text-[52px] lg:text-[60px] font-medium leading-[1.04] tracking-[-0.045em] text-[#111]">
            Engineering solutions
            <br />
            built for growth
          </h1>
          <p className="mt-5 mx-auto max-w-[520px] text-[15px] sm:text-base leading-relaxed text-[#111]/70">
            From custom software and web platforms to mobile apps and AI automation - end-to-end delivery, from architecture
            to launch and beyond.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PillButton to="/contact">Start a Project</PillButton>
            <PillButton to="/about" variant="light">
              About Me
            </PillButton>
          </div>
        </div>
      </Container>
    </section>

    {/* ── Service cards ── */}
    <section className="bg-[#e9ecf2] pb-12 lg:pb-16">
      <Container>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.map(({ title, desc, icon: Icon, tag }, i) => (
            <div
              key={title}
              className="group relative rounded-2xl bg-[#f4f6fa] p-6 min-h-[250px] flex flex-col transition-shadow hover:shadow-[0_20px_40px_rgba(17,24,58,0.08)]"
            >
              <span className="absolute right-6 top-5 text-[40px] leading-none font-semibold tracking-tight text-[#111]/[0.08]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="w-12 h-12 rounded-xl flex items-center justify-center text-white bg-[radial-gradient(120%_120%_at_25%_10%,#6f86ff_0%,#3b5bfd_55%,#1f33c9_100%)] shadow-[0_10px_20px_rgba(59,91,253,0.3)] transition-transform group-hover:-translate-y-1">
                <Icon className="w-5 h-5" />
              </span>
              <h2 className="mt-8 text-xl sm:text-[22px] font-medium tracking-[-0.03em] text-[#111]">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[#111]/65 flex-1">{desc}</p>
              <span className="mt-5 self-start bg-[#e6e9f0] rounded-full px-3.5 py-1.5 text-xs text-[#111]/70">{tag}</span>
            </div>
          ))}
        </div>
      </Container>
    </section>

    {/* ── Tech stack (navy) ── */}
    <section>
      <div className={NAVY_SECTION} style={navyIn}>
        <Container>
          <div className="text-center">
            <Eyebrow dark>Core Tooling</Eyebrow>
            <h2 className="mt-3 text-[34px] sm:text-[44px] lg:text-[48px] font-medium leading-[1.05] tracking-[-0.04em] text-white">
              Modern tools for fast,
              <br />
              resilient systems
            </h2>
          </div>

          {/* Plain grid straight on the navy background: no outer panel, no entrance animation */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {technologies.map(({ name, category, Icon, color }) => (
              <div
                key={name}
                className="group rounded-2xl bg-[#f4f6fa] p-3 sm:p-5 flex items-center gap-3 sm:gap-4 transition-transform hover:-translate-y-1"
              >
                <span
                  className="w-9 h-9 sm:w-11 sm:h-11 shrink-0 rounded-xl flex items-center justify-center text-lg sm:text-xl"
                  style={{ backgroundColor: `${color}1a`, color }}
                >
                  <Icon />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm sm:text-[15px] font-medium leading-snug tracking-[-0.01em] text-[#111]">{name}</span>
                  <span className="block text-[11px] sm:text-xs leading-snug text-[#111]/55">{category}</span>
                </span>
              </div>
            ))}
          </div>
        </Container>
      </div>
    </section>

    <Pricing />
    <Fade from={NAVY} to={LIGHT} className="h-28 lg:h-56" />
    <Faq />
  </>
);

export default AllServices;
